import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { eventAPI } from '../../lib/eventApi';
import { X, Play, Warning, CheckCircle } from '@phosphor-icons/react';

/**
 * Start Session preview modal.
 *
 * Loads /start-preview on open. Shows the active/sub split that
 * /start will produce, then commits via /start on confirm.
 *
 *   • Active roster picked by registered_at ASC.
 *   • Court seeding (inside the active set) by initial_rank.
 *   • Overflow players auto-flip to role='sub' at start time.
 */
export default function StartSessionDialog({ open, session, onClose, onStarted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !session) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    eventAPI.startPreview(session.id)
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || 'Could not load preview'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, session]);

  if (!open || !session) return null;

  const blocked = preview?.blocker_reason && preview.blocker_reason !== null;

  const handleStart = async () => {
    setStarting(true);
    try {
      await eventAPI.startSession(session.id);
      toast.success('Session started');
      onStarted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start session');
      setStarting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-2 sm:p-4"
      data-testid="start-session-dialog"
    >
      <div className="w-full sm:max-w-xl bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#e3dcd1] bg-white">
          <h2 className="font-serif text-2xl text-[#013523]">Start session</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={starting}
            data-testid="start-session-dialog-close"
            className="text-[#6a766e] hover:text-[#013523] p-1 rounded-lg disabled:opacity-50"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {loading && <div className="text-sm text-[#6a766e]">Loading preview…</div>}
          {err && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-900">{err}</div>
          )}

          {preview && (
            <>
              {/* Summary tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="start-summary-tiles">
                <Tile label="Registered" value={preview.registered_count} />
                <Tile
                  label="Courts used"
                  value={`${preview.courts_used} / ${preview.num_courts_configured}`}
                />
                <Tile label="Active" value={preview.active_count} accent="green" />
                <Tile
                  label="→ Subs"
                  value={preview.overflow_count}
                  accent={preview.overflow_count > 0 ? 'amber' : 'sand'}
                />
              </div>

              {/* Rules of the road */}
              <div className="bg-white border border-[#e3dcd1] rounded-xl p-3 text-xs text-[#2a332c] space-y-1">
                <p>First registered players get active roster priority.</p>
                <p>Initial rank determines court placement after the active roster is selected.</p>
                <p>Overflow players become subs when the session starts.</p>
              </div>

              {/* Blocker banner */}
              {blocked && (
                <div
                  data-testid="start-blocker-banner"
                  className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2"
                >
                  <Warning size={18} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-900">
                    {preview.blocker_reason === 'not_enough_players' && (
                      <>Need at least {preview.players_per_court} players to fill one court. Currently registered: {preview.registered_count}.</>
                    )}
                    {preview.blocker_reason === 'ranks_missing_or_dupes' && (
                      <>Every active player needs a unique initial_rank from 1 to {preview.active_count}. Fix the roster ranking and try again.</>
                    )}
                    {preview.blocker_reason === 'wrong_status' && (
                      <>Session status is {preview.status}; can't start from here.</>
                    )}
                  </div>
                </div>
              )}

              {/* Active list */}
              <RosterList
                title={`Active players (${preview.active_count})`}
                items={preview.active_players}
                testId="start-active-list"
                emptyText="No active players selected — register more to start."
                icon={<CheckCircle size={14} weight="fill" className="text-[#015e3f]" />}
              />

              {/* Overflow list */}
              {preview.overflow_count > 0 && (
                <RosterList
                  title={`Will become subs (${preview.overflow_count})`}
                  items={preview.overflow_players}
                  testId="start-overflow-list"
                  emptyText=""
                  amber
                />
              )}

              {/* Action row */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={starting}
                  data-testid="start-session-cancel"
                  className="flex-1 px-3 py-2 rounded-lg border border-[#e3dcd1] text-sm text-[#013523] hover:bg-[#f0ece3] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleStart}
                  disabled={blocked || starting}
                  data-testid="start-session-confirm"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#015e3f] text-white text-sm font-medium hover:bg-[#013523] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Play size={14} weight="fill" /> {starting ? 'Starting…' : 'Start session'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, accent = 'sand' }) {
  const tone = {
    green: 'bg-[#015e3f]/10 text-[#015e3f] border-[#015e3f]/20',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    sand:  'bg-white text-[#013523] border-[#e3dcd1]',
  }[accent];
  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-serif mt-0.5">{value}</div>
    </div>
  );
}

function RosterList({ title, items, testId, emptyText, icon, amber }) {
  return (
    <section data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-[#6a766e] mb-2">{title}</div>
      <ul className={`rounded-xl border ${amber ? 'border-amber-200 bg-amber-50/50' : 'border-[#e3dcd1] bg-white'} divide-y ${amber ? 'divide-amber-200' : 'divide-[#e3dcd1]'}`}>
        {items.length === 0 ? (
          <li className="px-3 py-2 text-xs text-[#6a766e]">{emptyText}</li>
        ) : (
          items.map((p) => (
            <li key={p.registration_id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-2 min-w-0">
                {icon}
                <span className="truncate text-[#013523]">{p.name}</span>
              </span>
              <span className="text-[11px] text-[#6a766e] shrink-0">
                {p.initial_rank ? `Rank #${p.initial_rank}` : 'No rank'}
              </span>
            </li>
          ))
        )}
      </ul>
    </section>
  );
}
